import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Axios from "../../api/axios/Axios";
import {
  UnauthorisedPageSurface,
  PageWrapper,
} from "../../components/layout/container/Container";
import { FormSection } from "../../components/layout/forms/Index";

import TextField from "../../components/input/textfield/TextField";
import TextArea from "../../components/input/textarea/TextArea.jsx";
import Select from "../../components/input/select/Select.jsx";

import StatusAlert from "../../components/alerts/StatusAlert.jsx";

import { PageHeader } from "../../components/layout/header/Header";
import { PageHeading } from "../../components/common/heading/Heading";
import FeedbackStatus from "../../components/feedback/FeedbackStatus";
import Button from "../../components/buttons/Button";
import "./SupportPage.css";

const SupportPage = () => {
  document.title = "Katch | Support";

  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    subject: "General",
    message: "",
  });

  const [status, setStatus] = useState({
    loading: false,
    success: false,
    error: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectedSubject = (value) => {
    const v = value.target.value;
    setFormData((prev) => ({ ...prev, subject: v }));
  };

  const validate = () => {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailPattern.test(formData.email))
      return "Please enter a valid email address.";
    if (formData.fullName.trim().length < 2) return "Please enter your name.";
    if (formData.message.trim().length < 10)
      return "Message must be at least 10 characters.";
    return null;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    const error = validate();
    if (error) return setStatus({ ...status, error });

    setStatus({ ...status, loading: true, error: "" });

    const submissionData = {
      name: formData.fullName.trim(),
      email: formData.email.trim(),
      subject: formData.subject.trim(),
      message: formData.message.trim(),
    };

    try {
      const res = await Axios.post("/api/v1/support", submissionData);
      if (res.data.success) {
        setStatus({ loading: false, success: true, error: "" });
      }
    } catch (err) {
      console.error(err);
      setStatus({
        loading: false,
        success: false,
        error: "Failed to send message. Please try again later.",
      });
    }
  };

  if (status.success) {
    return (
      <div className="support-page-content">
        <FeedbackStatus
          title={"Verify your email"}
          description={`Thank you for reaching out. We've received your inquiry and our
           support team will get back to you within 24 hours.`}
          buttonText={"Return Home"}
          onAction={() => navigate("/")}
        />
      </div>
    );
  }

  return (
    <UnauthorisedPageSurface>
      <PageWrapper size="md">
        <div className="support-page-content">
          <PageHeader>
            <PageHeading
              title={"Contact us"}
              subTitle={
                <span>
                  Need help? Send us a message below.{" "}
                  <Link
                    to="/sign-up"
                    style={{
                      color: "var(--ko-primary-blue-3)",
                      textDecoration: "none",
                    }}
                  >
                    Return Home
                  </Link>
                </span>
              }
            />
          </PageHeader>

          <FormSection>
            {status.error && (
              <StatusAlert
                status={{ status: "error", message: status.error }}
              />
            )}

            <TextField
              label="Your name"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              placeholder="Enter your full name"
              required
            />

            <TextField
              type="email"
              label="Your email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="email@domain.com"
              required
            />

            <Select
              label="Subject"
              value={formData.subject}
              items={[
                { value: "General", label: "General" },
                { value: "Technical support", label: "Technical support" },
                {
                  value: "Queries about payments",
                  label: "Queries about payments",
                },
                { value: "I can't log in", label: "I can't log in" },
                {
                  value: "Copyright infringement",
                  label: "Copyright infringement",
                },
                {
                  value: "I can't watch videos",
                  label: "I can't watch videos",
                },
                { value: "Account recovery", label: "Account recovery" },
                { value: "Privacy concerns", label: "Privacy concerns" },
                {
                  value: "Feedback submission",
                  label: "Feedback submission",
                },
                { value: "Other", label: "Other" },
              ]}
              onChange={handleSelectedSubject}
              required
            />

            <TextArea
              label="Message"
              placeholder="How can we help you today?"
              value={formData.message}
              name="message"
              rows={4}
              onChange={handleChange}
              required
            />

            <Button
              type="button"
              value={status.loading ? "Sending message..." : "Send message"}
              style={{ width: "100%", marginTop: "1rem" }}
              onClick={handleSubmit}
              disabled={status.loading}
            />
          </FormSection>
        </div>
      </PageWrapper>
    </UnauthorisedPageSurface>
  );
};

export default SupportPage;
